import React, { useEffect, useState, SyntheticEvent } from "react";
import { Button, Grid, TextField } from "@mui/material";
import StandardModal, { useModalLoading } from "./StandardModal";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { nowOperationalDateTimeString } from "../../utils/operationalDateTime";
import { useT } from '../../i18n';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  id: string;
}

const MyModal: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  onButtonClick,
  id,
}) => {
  const [userId, setUserId] = useState([]);
  const [location, setLocation] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [date, setDate] = useState("");
  const [value, setValue] = useState("");
  const [installmentValue, setInstallmentValue] = useState("");
  const [pendiente, setPendiente] = useState(0);
  const navigate = useNavigate();
  const t = useT();
  // Cargando propio del modal: consulta del abono y guardado.
  const { loading, showLoading, hideLoading } = useModalLoading();

  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  const calculatePendiente = () => {
    const pagar = parseInt(installmentValue) - parseInt(value) - parseInt(discountValue);
    setPendiente(pagar);
  };

  useEffect(() => {
    calculatePendiente();
  }, [value, discountValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('user');
        setUserId(data.data.id);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const paymentData = await axios.get(`payments/${id}`);
        setUserId(paymentData.data);
        setValue("0");
        setDiscountValue("0");
        setInstallmentValue(paymentData.data.loan.installmentValue);
      } catch (error) {
        console.error("Error fetching payment data:", error);
      }
    };

    const fetchCoordinates = async () => {
      try {
        const coords = await getCoordinates();
        setLocation(coords);
      } catch (error) {
        // handle error
      }
    };

    if (isOpen) {
      showLoading();
      fetchData().finally(hideLoading);
      fetchCoordinates();
    }
  }, [isOpen, id, showLoading, hideLoading]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
  
    const currentDate = nowOperationalDateTimeString();
  
    const formData = {
      balance: pendiente,
      paymentDate: currentDate,
      userId,
      state: true,
      value: parseInt(value),
      location,
      discountValue: parseInt(discountValue),
    };
  
    try {
      if (!navigator.onLine) {
        // Save data locally if offline
        localStorage.setItem(`pending-payment-${id}`, JSON.stringify(formData));
        // showAlert('Pago guardado localmente. Será enviado cuando el internet esté disponible.', 'info');
      } else {
        showLoading();
        await axios.put(`/payments/${id}`, formData).finally(hideLoading);
        // showAlert('Pago realizado exitosamente!', 'success');
        navigate(`/loans/payments/${id}/receipt`);
        onButtonClick();
      }
    } catch (error) {
      console.error("Error:", error);
      // //console.log(error?.response?.data?.message || 'Error during payment submission!', 'error');
    }
  };
  
  // Check for network restoration and post data if available
  useEffect(() => {
    const postSavedData = async () => {
      const savedData = localStorage.getItem(`pending-payment-${id}`);
      if (savedData) {
        try {
          await axios.put(`/payments/${id}`, JSON.parse(savedData));
          // showAlert('Pago enviado exitosamente desde datos locales!', 'success');
          localStorage.removeItem(`pending-payment-${id}`);
          navigate(`/loans/payments/${id}/receipt`);
          onButtonClick();
        } catch (error) {
          // showAlert(error?.response?.data?.message || 'Failed to submit offline payment data!', 'error');
        }
      }
    };
  
    // Debounce online handler to avoid multiple submissions on unstable connections
    let onlineHandlerTimeout: NodeJS.Timeout;
    const debouncedPost = () => {
      clearTimeout(onlineHandlerTimeout);
      onlineHandlerTimeout = setTimeout(postSavedData, 1000);
    };
  
    window.addEventListener('online', debouncedPost);
    
    return () => {
      clearTimeout(onlineHandlerTimeout);
      window.removeEventListener('online', debouncedPost);
    };
  }, [id]);

  return (
    <StandardModal
      open={isOpen}
      onClose={onClose}
      title={t('payments.quick.title', { id })}
      maxWidth={800}
      loading={loading}
      actions={
        <>
          <Button variant="outlined" color="secondary" size="small" onClick={onButtonClick}>
            {t('payments.quick.close')}
          </Button>
          <Button variant="contained" size="small" onClick={submit}>
            {t('payments.quick.save')}
          </Button>
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={t('payments.quick.installment')}
            value={installmentValue}
            onChange={(event) => setInstallmentValue(event.target.value)}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={t('payments.quick.amount')}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={t('payments.quick.discount')}
            value={discountValue}
            onChange={(event) => setDiscountValue(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label={t('payments.quick.pending')} value={pendiente || "0"} disabled />
        </Grid>
      </Grid>
    </StandardModal>
  );
};

export default MyModal;
